import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import { parentApi, ApiError, type ParentChild } from '@/lib/parent-api'
import { TimelineView } from '@/components/timeline'
import { UPDATE_TYPES, UPDATE_TYPE_LABELS, type Update, type UpdateType } from '@/types/update'

export function ParentChildDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [child, setChild] = useState<ParentChild | null>(null)
  const [updates, setUpdates] = useState<Update[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [nextCursor, setNextCursor] = useState<string | null>(null)

  const [filterType, setFilterType] = useState<UpdateType | 'all'>('all')

  useEffect(() => {
    if (id) {
      loadData()
    }
  }, [id])

  useEffect(() => {
    if (id && child) {
      loadUpdates()
    }
  }, [filterType, id, child])

  const loadData = async () => {
    try {
      setIsLoading(true)
      const [childRes, updatesRes] = await Promise.all([
        parentApi.getChild(id!),
        parentApi.getUpdates({ childId: id }),
      ])
      setChild(childRes.child)
      setUpdates(updatesRes.updates)
      setHasMore(updatesRes.hasMore)
      setNextCursor(updatesRes.nextCursor)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load data')
    } finally {
      setIsLoading(false)
    }
  }

  const loadUpdates = async () => {
    try {
      const params: { childId: string; type?: string } = { childId: id! }
      if (filterType !== 'all') params.type = filterType

      const response = await parentApi.getUpdates(params)
      setUpdates(response.updates)
      setHasMore(response.hasMore)
      setNextCursor(response.nextCursor)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load updates')
    }
  }

  const loadMore = async () => {
    if (!nextCursor || isLoadingMore) return

    try {
      setIsLoadingMore(true)
      const params: { childId: string; type?: string; before: string } = {
        childId: id!,
        before: nextCursor,
      }
      if (filterType !== 'all') params.type = filterType

      const response = await parentApi.getUpdates(params)
      setUpdates((prev) => [...prev, ...response.updates])
      setHasMore(response.hasMore)
      setNextCursor(response.nextCursor)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load more updates')
    } finally {
      setIsLoadingMore(false)
    }
  }

  if (isLoading) {
    return <div className="p-4">Loading...</div>
  }

  if (!child) {
    return (
      <div className="p-4">
        <p className="text-red-600">{error || 'Child not found or access denied'}</p>
        <button onClick={() => navigate('/timeline')} className="mt-2 text-primary-600">
          Back to timeline
        </button>
      </div>
    )
  }

  return (
    <div className="p-4">
      <button
        onClick={() => navigate('/timeline')}
        className="mb-4 text-sm text-gray-500 hover:text-gray-700"
      >
        ← Back to timeline
      </button>

      <h1 className="mb-2 text-2xl font-bold ph-no-capture">
        {child.firstName} {child.lastName}
      </h1>

      {child.dateOfBirth && (
        <p className="mb-4 text-gray-500">
          Born: {new Date(child.dateOfBirth).toLocaleDateString()}
        </p>
      )}

      {error && (
        <div className="mb-4 rounded bg-red-100 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Filter */}
      <section className="mb-4">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilterType('all')}
            className={`rounded-full px-3 py-1 text-sm ${
              filterType === 'all'
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All Updates
          </button>
          {UPDATE_TYPES.map((t) => (
            <button
              key={t}
              onClick={() => setFilterType(t)}
              className={`rounded-full px-3 py-1 text-sm ${
                filterType === t
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {UPDATE_TYPE_LABELS[t]}
            </button>
          ))}
        </div>
      </section>

      {/* Timeline */}
      <section>
        <h2 className="mb-4 text-lg font-medium">Timeline</h2>
        {updates.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-gray-500">
            <p className="font-medium">
              {filterType === 'all'
                ? 'No updates yet'
                : `No ${UPDATE_TYPE_LABELS[filterType].toLowerCase()} updates`}
            </p>
            <p className="mt-1 text-sm">
              Updates from the childminder will appear here
            </p>
          </div>
        ) : (
          <TimelineView
            updates={updates}
            showChildren={false}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            onLoadMore={loadMore}
          />
        )}
      </section>
    </div>
  )
}
